import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { queryClient } from '@/lib/query-client'
// Import route tree - TanStack Router will generate this
import { routeTree } from './routeTree.gen'
import { Toaster } from 'sonner'
import { ErrorBoundary } from 'react-error-boundary'
import { AppProvider } from '@/lib/store'
import './styles/globals.css'

// Create router instance
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

function ErrorFallback({ error }: { error: Error }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-card border border-border rounded-lg p-6 text-center">
        <h2 className="text-2xl font-bold text-foreground mb-4">
          Something went wrong
        </h2>
        <p className="text-muted-foreground mb-6">
          {error.message || 'An unexpected error occurred'}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          Reload Page
        </button>
      </div>
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <AppProvider>
        <RouterProvider router={router} />
      </AppProvider>
      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          style: {
            background: '#1f2937',
            border: '1px solid #374151',
            color: '#f9fafb',
          },
        }}
      />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </ErrorBoundary>
  )
}

const root = document.getElementById('root')

if (!root) {
  throw new Error('Root element not found')
}

import { QueryClientProvider } from '@tanstack/react-query'
import { ReactNode } from 'react'

function Providers({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  )
}

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

createRoot(root!).render(
  <StrictMode>
    <Providers>
      <App />
    </Providers>
  </StrictMode>
)